import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { GitBranch, ExternalLink, Download } from 'lucide-react';
import PageLayout from '@components/layout/PageLayout';
import ListPageHeader from '@components/common/ListPageHeader';
import Button from '@components/common/Button';
import Badge from '@components/common/Badge';
import { RZTable, DateCell, ColumnSelector } from '@components/common/table';
import ErrorState from '@components/common/ErrorState';
import { usePermissions } from '@contexts/AuthContext';
import { conflictService } from '@services';
import { useDebounce } from '@hooks/useDebounce';
import { usePagination } from '@hooks/usePagination';
import { useColumnFilters } from '@hooks/useColumnFilters';
import { useTableSort } from '@hooks/useTableSort';
import { CONFLICT_TYPE_CODE, type Conflict, type ConflictExportFilters } from '@app-types/conflict';
import type { TableColumn } from '@app-types';
import { formatInstantAsDate, getBrowserTimeZone } from '@utils/dateUtils';
import { exportDivisionConflicts } from '@utils/exportUtils';
import { CONTRACT_TEXT_FILTER_MAX_LENGTH } from '@utils/columnFilterUrl';
import { getApiErrorMessage } from '@utils/apiError';
import { showToast } from '@utils/toast';
import {
  DIVISION_CONFLICT_COLUMN_LABELS,
  type DivisionConflictContract,
  getConflictContracts,
  getConflictOwners,
  getConflictSuppliers,
  isMeetingConflict
} from '@utils/divisionConflictUtils';
import styles from './DivisionConflictWorklistPage.module.css';

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const VISIBLE_COLUMNS_STORAGE_KEY = 'division-conflicts-visible-columns';

const conflictColumns: TableColumn[] = [
  {
    key: 'contract_number',
    label: DIVISION_CONFLICT_COLUMN_LABELS.contract_number,
    defaultVisible: true,
    alwaysVisible: true,
    filterKey: 'contract_number',
    maxFilterLength: CONTRACT_TEXT_FILTER_MAX_LENGTH
  },
  {
    key: 'contract_name',
    label: DIVISION_CONFLICT_COLUMN_LABELS.contract_name,
    defaultVisible: true,
    filterKey: 'contract_name',
    maxFilterLength: CONTRACT_TEXT_FILTER_MAX_LENGTH
  },
  {
    key: 'supplier',
    label: DIVISION_CONFLICT_COLUMN_LABELS.supplier,
    defaultVisible: true,
    filterKey: 'supplier',
    maxFilterLength: CONTRACT_TEXT_FILTER_MAX_LENGTH
  },
  // Not sortable: owners are a to-many relation, so the API has no meaningful order for them.
  {
    key: 'owners',
    label: DIVISION_CONFLICT_COLUMN_LABELS.owners,
    defaultVisible: true,
    filterKey: 'owners',
    sortable: false,
    maxFilterLength: CONTRACT_TEXT_FILTER_MAX_LENGTH
  },
  { key: 'detected', label: DIVISION_CONFLICT_COLUMN_LABELS.detected, defaultVisible: true, filterKey: 'detected', filterType: 'dateRange' },
  {
    key: 'action',
    label: '',
    defaultVisible: true,
    alwaysVisible: true,
    hiddenFromSelector: true,
    filterable: false,
    sortable: false
  }
];

// The API sorts by the same key the column has, so the sortable columns are the mapping.
const SORT_COLUMN_MAPPING: Record<string, string> = Object.fromEntries(
  conflictColumns.filter((col) => col.sortable !== false).map((col) => [col.key, col.key])
);

// Meeting-level conflicts are resolved from the Scheduler, where the bundle lives. Program Lead has no Scheduler
// access, so it works from the Assessment Meetings list instead (ADO-1176).
const getConflictRoute = (conflict: Conflict, canAccessScheduler: boolean): string => {
  if (!isMeetingConflict(conflict)) return `/contracts/${conflict.entity_id}`;

  const params = new URLSearchParams({ myMeetings: 'false', status: 'conflicts' });
  const firstContractNumber = conflict.entity_info?.contracts?.[0]?.contract_number;
  if (firstContractNumber) params.set('search', firstContractNumber);
  return `/${canAccessScheduler ? 'scheduler' : 'assessor/meetings'}?${params.toString()}`;
};

const loadVisibleColumns = (): Set<string> => {
  try {
    const stored = localStorage.getItem(VISIBLE_COLUMNS_STORAGE_KEY);
    if (stored) return new Set(JSON.parse(stored));
  } catch {
    // Unreadable preference: fall back to the defaults.
  }
  return new Set(conflictColumns.filter((col) => col.defaultVisible).map((col) => col.key));
};

const DivisionConflictWorklistPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canAccessScheduler, canAccessSuppliers } = usePermissions();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');

  const { currentPage, pageSize, setCurrentPage, setPageSize, resetToFirstPage } = usePagination({
    storageKey: 'division-conflicts-page-size',
    defaultPageSize: PAGE_SIZE_OPTIONS[0]
  });

  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // Keep the search in the URL so the list can be shared and survives a reload. Other params (column filters,
  // pagination) belong to their own hooks and are preserved.
  useEffect(() => {
    if ((searchParams.get('search') ?? '') === debouncedSearchQuery) return;

    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (debouncedSearchQuery) {
          params.set('search', debouncedSearchQuery);
        } else {
          params.delete('search');
        }
        return params;
      },
      { replace: true }
    );
  }, [debouncedSearchQuery, searchParams, setSearchParams]);

  const {
    filterValues: columnFilterValues,
    debouncedFilterValues: debouncedColumnFilters,
    setFilter: setColumnFilter,
    clearAllFilters: clearColumnFilters,
    hasActiveFilters: hasActiveColumnFilters,
    getApiParam: getColumnFiltersParam
  } = useColumnFilters({ columns: conflictColumns });

  const clearAllFilters = () => {
    clearColumnFilters();
    setSearchQuery('');
    resetToFirstPage();
  };

  const hasAnyActiveFilter = hasActiveColumnFilters || searchQuery !== '';

  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(loadVisibleColumns);

  useEffect(() => {
    try {
      localStorage.setItem(VISIBLE_COLUMNS_STORAGE_KEY, JSON.stringify(Array.from(visibleColumns)));
    } catch {
      // The preference just isn't remembered.
    }
  }, [visibleColumns]);

  const toggleColumn = (columnKey: string) => {
    if (conflictColumns.find((col) => col.key === columnKey)?.alwaysVisible) return;

    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(columnKey)) {
        next.delete(columnKey);
      } else {
        next.add(columnKey);
      }
      return next;
    });
  };

  const renderedColumns = conflictColumns.filter((col) => col.alwaysVisible || visibleColumns.has(col.key));

  // Reset to page 1 when the result set changes
  useEffect(() => {
    resetToFirstPage();
  }, [debouncedSearchQuery, debouncedColumnFilters, resetToFirstPage]);

  // No sort until the user picks a column, so the API keeps its default order: open before acknowledged, newest first
  const { sortConfig, handleSort, isSorted } = useTableSort({
    columnMapping: SORT_COLUMN_MAPPING,
    defaultSort: { field: '', direction: 'DESC' },
    onPageReset: () => setCurrentPage(1)
  });

  const buildFilters = (): ConflictExportFilters => ({
    conflictTypeCode: CONFLICT_TYPE_CODE.DIVISION_CONFLICT,
    search: debouncedSearchQuery || undefined,
    columnFilters: getColumnFiltersParam() || undefined,
    sortBy: sortConfig.field || undefined,
    order: sortConfig.field ? (sortConfig.direction === 'ASC' ? 'asc' : 'desc') : undefined,
    // The Detected filter matches days as the viewer sees them on the page
    timezone: getBrowserTimeZone()
  });

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: [
      'conflicts-worklist',
      CONFLICT_TYPE_CODE.DIVISION_CONFLICT,
      debouncedSearchQuery,
      debouncedColumnFilters,
      sortConfig,
      currentPage,
      pageSize
    ],
    // Keep the table (and the focused filter input) mounted while a new search, filter or sort loads
    placeholderData: keepPreviousData,
    queryFn: () =>
      conflictService.getWorklist({
        ...buildFilters(),
        limit: pageSize,
        offset: (currentPage - 1) * pageSize
      })
  });

  // Export fetches the full filtered set (all pages), not just the current page
  const exportMutation = useMutation({
    mutationFn: () => conflictService.getWorklistForExport(buildFilters()),
    onSuccess: (response) => {
      exportDivisionConflicts(response.data, renderedColumns.map((col) => col.key));
    },
    onError: (err: unknown) => {
      console.error('Failed to export division conflicts:', err);
      showToast.error(getApiErrorMessage(err, 'Failed to export division conflicts. Please try again.'));
    }
  });

  const conflicts = data?.conflicts ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const renderContractLink = (contract: DivisionConflictContract) => (
    <Link key={contract.id} to={`/contracts/${contract.id}`} className={styles.tableLink}>
      {contract.contract_number}
    </Link>
  );

  const renderSupplier = (id: number | null, name: string) =>
    canAccessSuppliers && id !== null ? (
      <Link to={`/suppliers/${id}`} className={styles.tableLink}>
        {name}
      </Link>
    ) : (
      name
    );

  // In a bundle every contract gets one fixed-height line in the number, name and supplier cells, so the
  // three columns stay row-aligned however long a name is (it truncates, the full text is in the title).
  const renderContractNumberCell = (conflict: Conflict) => {
    const contracts = getConflictContracts(conflict);
    if (contracts.length === 0) return '-';

    if (!isMeetingConflict(conflict)) return contracts.map(renderContractLink);

    return (
      <div className={styles.bundleLines}>
        {contracts.map((contract, index) => (
          <div key={contract.id} className={styles.bundleLine}>
            {renderContractLink(contract)}
            {index === 0 && <Badge variant="default">Meeting bundle</Badge>}
          </div>
        ))}
      </div>
    );
  };

  const renderContractNameCell = (conflict: Conflict) => {
    const contracts = getConflictContracts(conflict);
    if (contracts.length === 0) return '-';

    if (isMeetingConflict(conflict)) {
      return (
        <div className={styles.bundleLines}>
          {contracts.map((contract) => (
            <div key={contract.id} className={styles.bundleLine}>
              <span className={styles.truncate} title={contract.name || undefined}>
                {contract.name || '-'}
              </span>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className={styles.stackedCell}>
        {contracts.map((contract) => (
          <div key={contract.id}>{contract.name || '-'}</div>
        ))}
      </div>
    );
  };

  const renderSupplierCell = (conflict: Conflict) => {
    if (isMeetingConflict(conflict)) {
      const contracts = getConflictContracts(conflict);
      if (contracts.length === 0) return '-';
      return (
        <div className={styles.bundleLines}>
          {contracts.map((contract) => (
            <div key={contract.id} className={styles.bundleLine}>
              <span className={styles.truncate} title={contract.supplier_name ?? undefined}>
                {contract.supplier_name ? renderSupplier(contract.supplier_id, contract.supplier_name) : '-'}
              </span>
            </div>
          ))}
        </div>
      );
    }

    const suppliers = getConflictSuppliers(conflict);
    if (suppliers.length === 0) return '-';
    return (
      <div className={styles.stackedCell}>
        {suppliers.map((supplier) => (
          <div key={supplier.id ?? supplier.name}>{renderSupplier(supplier.id, supplier.name)}</div>
        ))}
      </div>
    );
  };

  const renderCell = (column: TableColumn, conflict: Conflict) => {
    switch (column.key) {
      case 'contract_number':
        return renderContractNumberCell(conflict);

      case 'contract_name':
        return renderContractNameCell(conflict);

      case 'supplier':
        return renderSupplierCell(conflict);

      case 'owners': {
        const owners = getConflictOwners(conflict);
        if (owners.length === 0) return '-';
        return (
          <div className={styles.ownersCell}>
            {owners.map((owner) => (
              <div key={owner.user_id} className={styles.ownerRow}>
                <span>{owner.user_name}</span>
                <Badge variant="default">{owner.division_name ?? 'No Division'}</Badge>
              </div>
            ))}
          </div>
        );
      }

      case 'detected':
        return <DateCell date={conflict.detected_at} formatDate={(d) => formatInstantAsDate(d, 'Unknown')} />;

      case 'action': {
        const label = isMeetingConflict(conflict) ? 'Open in Scheduler' : 'Open Contract';
        return (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(getConflictRoute(conflict, canAccessScheduler))}
            title={label}
            aria-label={label}
          >
            <ExternalLink size={16} />
          </Button>
        );
      }

      default:
        return '-';
    }
  };

  if (error) {
    return (
      <PageLayout title="Division Conflict Resolution Worklist">
        <ErrorState message="Error loading division conflicts. Please try again." />
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Division Conflict Resolution Worklist">
      <ListPageHeader
        icon={<GitBranch size={24} />}
        title="Division Conflicts"
        count={total}
        searchValue={searchQuery}
        onSearchChange={(value) => {
          setSearchQuery(value);
          setCurrentPage(1);
        }}
        searchPlaceholder="Search contracts, suppliers, owners..."
        actions={
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => exportMutation.mutate()}
              disabled={exportMutation.isPending || conflicts.length === 0}
            >
              <Download size={16} />
              {exportMutation.isPending ? 'Exporting...' : 'Export List'}
            </Button>

            <ColumnSelector
              columns={conflictColumns}
              visibleColumns={renderedColumns.map((col) => col.key)}
              onToggleColumn={toggleColumn}
              dataTestId="column-selector"
            />
          </>
        }
      />

      <RZTable
        data={conflicts}
        columns={renderedColumns}
        getRowKey={(conflict) => conflict.id}
        renderCell={renderCell}
        sorting={{ sortConfig, onSort: handleSort, isSorted }}
        columnFilters={{
          values: columnFilterValues,
          onChange: setColumnFilter,
          onClearAll: clearAllFilters,
          hasActiveFilters: hasAnyActiveFilter
        }}
        pagination={{
          currentPage,
          totalPages,
          totalItems: total,
          pageSize,
          onPageChange: setCurrentPage,
          onPageSizeChange: setPageSize,
          itemName: 'conflicts',
          pageSizeOptions: PAGE_SIZE_OPTIONS
        }}
        state={{
          isLoading,
          isFetching,
          isEmpty: conflicts.length === 0,
          emptyTitle: hasAnyActiveFilter ? 'No division conflicts found' : 'No division conflicts',
          emptyDescription: hasAnyActiveFilter
            ? 'Try changing filters or search criteria'
            : 'All contracts have consistent divisions across their contract owners.',
          loadingMessage: 'Loading division conflicts...'
        }}
      />
    </PageLayout>
  );
};

export default DivisionConflictWorklistPage;
